/**
 * SUP-ADMIN-UI-01 — narrow domain types for the platform-admin bug-report
 * triage surface, sourced from the generated `support/v1` contract types
 * (`@aeriosiq/types`, `Support*` exports). Re-exported here so the
 * feature's hooks/components import from one local module rather than reaching
 * into the codegen surface directly (mirrors the tenancy feature's types.ts).
 */
import type { SupportComponents } from "@aeriosiq/types";

type Schemas = SupportComponents["schemas"];

/** Triage lifecycle — `open` | `triaged` | `in_progress` | `resolved` | `closed`. */
export type BugReportStatus = Schemas["BugReportStatus"];
/** Report kind — `bug` | `support` | `incident`. */
export type BugReportKind = Schemas["BugReportKind"];
/** Reporter-asserted severity — `low` | `medium` | `high` | `critical`. */
export type BugReportSeverity = Schemas["BugReportSeverity"];
/** Diagnostic-zip attachment lifecycle — `pending` | `uploaded` | `verified`. */
export type AttachmentState = Schemas["AttachmentState"];

/** Lightweight triage-list row (GET /bug-reports items). */
export type BugReportSummary = Schemas["BugReportSummary"];
/** A page of triage-list rows + opaque next-page cursor. */
export type BugReportList = Schemas["BugReportList"];
/** Full bug-report detail (GET /bug-reports/{id}). */
export type BugReport = Schemas["BugReport"];
/** One attachment as surfaced in the detail read (with read SAS). */
export type BugReportAttachment = Schemas["BugReportAttachment"];
/** Who filed the report. */
export type Reporter = Schemas["Reporter"];
/** Partial triage update (PATCH /bug-reports/{id}). */
export type BugReportPatch = Schemas["BugReportPatch"];

/** Filters for the triage list. All optional; compose with AND server-side. */
export interface BugReportListFilters {
  status?: BugReportStatus;
  tenantId?: string;
  kind?: BugReportKind;
  severity?: BugReportSeverity;
  createdFrom?: string;
  createdTo?: string;
  cursor?: string;
  limit?: number;
}

export const BUG_REPORT_STATUSES: readonly BugReportStatus[] = [
  "open",
  "triaged",
  "in_progress",
  "resolved",
  "closed",
];

export const BUG_REPORT_KINDS: readonly BugReportKind[] = [
  "bug",
  "support",
  "incident",
];

export const BUG_REPORT_SEVERITIES: readonly BugReportSeverity[] = [
  "low",
  "medium",
  "high",
  "critical",
];
